import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { CRESTMONT } from './CrestmontChrome';
import { getUniversityBySlug } from '../../data/universityData';
import { getPublisherParams } from '../../utils/publisherParams';
import { getMetaCookies, resolveSub3 } from '../../utils/metaCookies';
import { trackFormSubmit } from '../../utils/trackFormSubmit';
import { validateAddress } from '../../utils/addressValidation';
import { fetchClientIp, prefetchClientIp } from '../../utils/clientIp';
import { getUrlPassthroughParams } from '../../utils/urlPassthrough';
import { injectLeadCertificationScripts } from '../../utils/leadCertificationScripts';

// Compact two-step request form used on the Crestmont pages.
//
// Step 1 asks the two qualifying questions (degree of interest, highest level
// of education). Step 2 asks for contact details. Splitting them keeps the
// commitment low on first sight — the visitor answers two dropdowns before
// being asked for a phone number — and means an abandoned form still tells us
// which degree drew the interest.

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
}
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// prettier-ignore
const STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

// Same option set and API codes used across the other lead forms.
const EDUCATION_OPTIONS: {
  label: string;
  value: string;
}[] = [{
  label: 'High School Diploma/GED',
  value: 'high-school'
}, {
  label: 'Some College',
  value: 'some-college'
}, {
  label: "Associate's Degree",
  value: 'associates'
}, {
  label: "Bachelor's Degree",
  value: 'bachelors'
}, {
  label: 'Some High School',
  value: 'some-high-school'
}];
const educationToApiValue = (value: string): string => {
  switch (value) {
    case 'high-school':
      return '1';
    case 'some-high-school':
      return '2';
    case 'some-college':
    case 'associates':
      return '5';
    case 'bachelors':
      return '7';
    default:
      return '9';
  }
};
interface CrestmontLeadFormProps {
  /** Pre-selected program, posted as mProgram. */
  programValue: string;
  programName: string;
  /** Other Crestmont degrees the visitor can switch to. */
  programOptions: {
    label: string;
    value: string;
  }[];
  heading?: string;
  subheading?: string;
}
export function CrestmontLeadForm({
  programValue,
  programName,
  programOptions,
  heading = 'Request Program Information',
  subheading
}: CrestmontLeadFormProps) {
  const university = getUniversityBySlug('crestmont-university');
  const sessionIdRef = useRef<string>('');
  if (!sessionIdRef.current) sessionIdRef.current = `s-${generateUUID()}`;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    program: programValue,
    highestEducation: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [addressChecking, setAddressChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Keep the dropdown in sync when the visitor moves between program pages.
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      program: programValue
    }));
  }, [programValue]);

  // LeadiD + TrustedForm consent certification. Both scripts write into the
  // hidden inputs rendered below, which stay mounted across both steps.
  useEffect(() => injectLeadCertificationScripts(), []);

  // Resolve the visitor's public IP up front so it is ready at submit time.
  useEffect(() => prefetchClientIp(), []);
  const update = (field: keyof typeof form, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };
  const handleQualifyingContinue = () => {
    if (!form.program) {
      setError('Please choose the degree you are interested in.');
      return;
    }
    if (!form.highestEducation) {
      setError('Please select your highest level of education.');
      return;
    }
    setError('');
    setStep(1);
  };

  // Same hygiene checks the other lead forms run: plausible street line, a ZIP
  // that actually exists, and a state that agrees with it.
  const handleAddressContinue = async () => {
    if (!form.city.trim()) {
      setError('Please enter your city.');
      return;
    }
    if (!form.state) {
      setError('Please select your state.');
      return;
    }
    setAddressChecking(true);
    const check = await validateAddress({
      streetAddress: form.streetAddress,
      zipCode: form.zipCode,
      state: form.state
    });
    setAddressChecking(false);
    if (!check.valid) {
      setError(check.message);
      return;
    }
    setError('');
    setStep(2);
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (step === 0) {
      handleQualifyingContinue();
      return;
    }
    if (step === 1) {
      await handleAddressContinue();
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (form.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!consent) {
      setError('Please agree to be contacted before submitting.');
      return;
    }
    setIsSubmitting(true);
    const zip = form.zipCode.replace(/\D/g, '');
    // Usually already cached from the page-load prefetch; resolves to '' if the
    // lookup is blocked, which must never hold up the lead.
    const ipAddress = await fetchClientIp();
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const selectedProgram = programOptions.find(option => option.value === form.program);
    const selectedEducation = EDUCATION_OPTIONS.find(option => option.value === form.highestEducation);
    const payload: Record<string, string> = {
      // Everything the landing URL carried, forwarded verbatim. Listed first
      // so the resolved values below always take precedence.
      ...getUrlPassthroughParams(window.location.search),
      ...getPublisherParams(window.location.search, {
        mPubID: university?.mPubID || '',
        mPubCampaignID: university?.mPubCampaignID || ''
      }),
      mBrandID: university?.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: sessionIdRef.current,
      leadIdToken,
      trustedFormUrl: tfInput?.value || '',
      mProgram: form.program,
      // Readable labels alongside the codes — the Zapier hook feeds a
      // spreadsheet/CRM, where raw ids are not actionable on their own.
      programName: selectedProgram?.label || programName,
      degreeInterest: '',
      areaStudy: '',
      highestEducation: educationToApiValue(form.highestEducation),
      highestEducationLabel: selectedEducation?.label || '',
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.replace(/\D/g, ''),
      address: form.streetAddress.trim().replace(/\s+/g, ' '),
      zipCode: zip,
      city: form.city.trim(),
      state: form.state,
      ipAddress,
      useragent: navigator.userAgent,
      sub3: resolveSub3(window.location.search),
      webLandingURL: window.location.href,
      // Record the affirmative consent alongside the disclosure the user saw.
      tcpaConsent: '1',
      tcpaText: university?.tcpaText || ''
    };
    const {
      fbp,
      fbc
    } = getMetaCookies();
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    console.log('[Crestmont Program Lead] payload:', payload);
    let reported = false;
    const reportSubmission = () => {
      if (reported) return;
      reported = true;
      trackFormSubmit({
        formId: 'crestmont-program-request-info',
        pageUrl: window.location.pathname,
        leadId: leadIdToken
      });
    };
    const postBase = university?.leadPostUrl || '';
    if (postBase) {
      const fullUrl = `${postBase}?${new URLSearchParams(payload).toString()}`;
      console.log('[Crestmont Program Lead] POST URL:', fullUrl);
      // Fired two ways: a keepalive request survives the visitor navigating
      // away, and the pixel covers browsers that block the fetch.
      try {
        fetch(fullUrl, {
          method: 'GET',
          mode: 'no-cors',
          keepalive: true
        }).then(reportSubmission, () => {});
      } catch (err) {
        console.error('[Crestmont Program Lead] fetch error:', err);
      }
      try {
        const pixel = new Image();
        pixel.onload = reportSubmission;
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Crestmont Program Lead] post error:', err);
      }
    } else {
      console.log('[Crestmont Program Lead] No leadPostUrl configured — skipping post.');
    }
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      reportSubmission();
    }, 700);
  };

  // 16px type on mobile keeps iOS from zooming the viewport on focus; the
  // taller mobile control also gives a comfortable touch target.
  const inputClass = 'w-full px-3.5 py-3 sm:py-2.5 text-base sm:text-sm bg-white border border-gray-300 rounded text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00426b] focus:ring-1 focus:ring-[#00426b] transition-colors';
  const labelClass = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5';
  if (isSuccess) {
    return <div className="bg-white border border-gray-200 rounded-lg p-8 text-center" data-id="element-346" data-mp="3212z8tacaw8z">
        <CheckCircleIcon className="w-10 h-10 mx-auto mb-4" style={{
        color: CRESTMONT.navy
      }} aria-hidden="true" data-id="element-347" data-mp="1vmk848m1m0lx" />
        <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-348" data-mp="1qp26l3acunny">
          Request received
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-349" data-mp="62u2qkmmvy09">
          A Crestmont advisor will reach out about the {programName} with program
          details, transfer credit, and next steps.
        </p>
      </div>;
  }
  return <form id="apply" onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6 md:p-7" noValidate data-id="element-350" data-mp="kubcn6oznqym">
      {/* Populated by the LeadiD and TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-351" data-mp="ttgccgcadqcd" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-352" data-mp="ytkra1xjqd0h" />

      <div className="flex items-baseline justify-between gap-3" data-id="element-353" data-mp="yudpgeifvu70">
        <h2 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-354" data-mp="13rmacmuz2cby">
          {heading}
        </h2>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 whitespace-nowrap" data-id="element-355" data-mp="30lz6gt8ph3d7">
          Step {step + 1} of 3
        </span>
      </div>
      <p className="mt-1.5 text-xs text-gray-500 leading-relaxed" data-id="element-356" data-mp="2xj2svdtlr7i4">
        {step === 0 ? 'Start with two quick questions so an advisor can prepare the right program details.' : step === 1 ? 'Where are you located? This helps us confirm program availability in your area.' : subheading || `Tell us where to reach you and an advisor will follow up about the ${programName}.`}
      </p>

      {step === 0 ? <div className="mt-4 sm:mt-5 space-y-3" data-id="element-357" data-mp="3gg40l6ejjlpr">
          <div data-id="element-358" data-mp="3cheqbmosukac">
            <label htmlFor="cu-program" className={labelClass} data-id="element-359" data-mp="3cfk1hxaeq80z">
              Degree of interest
            </label>
            <select id="cu-program" value={form.program} onChange={e => update('program', e.target.value)} className={inputClass} data-id="element-360" data-mp="3q7i8o2kk5tui">
              {programOptions.map(option => <option key={option.value} value={option.value} data-id="element-361" data-mp="3v7mpjtpdoql9">
                  {option.label}
                </option>)}
            </select>
          </div>
          <div data-id="element-362" data-mp="ilo2xddkfcj1">
            <label htmlFor="cu-education" className={labelClass} data-id="element-363" data-mp="mou26129g3g0">
              Highest level of education
            </label>
            <select id="cu-education" value={form.highestEducation} onChange={e => update('highestEducation', e.target.value)} className={inputClass} data-id="element-364" data-mp="8o9kx9bzufom">
              <option value="" data-id="element-365" data-mp="3v3cjkte3cvca">-- Please select --</option>
              {EDUCATION_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-366" data-mp="3rj8qx3b848h">
                  {option.label}
                </option>)}
            </select>
          </div>
        </div> : step === 1 ? <div className="mt-4 sm:mt-5 space-y-3" data-id="element-367" data-mp="3r0xxm0f4lfgs">
          <div data-id="element-368" data-mp="3c72u79xqoicz">
            <label htmlFor="cu-street" className="sr-only" data-id="element-369" data-mp="3c6l28pi40qav">
              Street address
            </label>
            <input id="cu-street" type="text" autoComplete="street-address" placeholder="Street address" value={form.streetAddress} onChange={e => update('streetAddress', e.target.value)} className={inputClass} data-id="element-370" data-mp="3h4cszg70q6dh" />
          </div>
          <div data-id="element-371" data-mp="9m19qukv9j2n">
            <label htmlFor="cu-city" className="sr-only" data-id="element-372" data-mp="9mjfqixbzbnr">
              City
            </label>
            <input id="cu-city" type="text" autoComplete="address-level2" placeholder="City" value={form.city} onChange={e => update('city', e.target.value)} className={inputClass} data-id="element-373" data-mp="4n6kpawb2ya3" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-374" data-mp="3mt2xpzwyw5u8">
            <div data-id="element-375" data-mp="1pzzr3uc0teo0">
              <label htmlFor="cu-state" className="sr-only" data-id="element-376" data-mp="1q0jbrit22h5q">
                State
              </label>
              <select id="cu-state" autoComplete="address-level1" value={form.state} onChange={e => update('state', e.target.value)} className={inputClass} data-id="element-377" data-mp="1l0n6zhcjalp4">
                <option value="" data-id="element-378" data-mp="1xhfxqjd1bdac">State</option>
                {STATES.map(s => <option key={s} value={s} data-id="element-379" data-mp="22b0u66yzbz7f">
                    {s}
                  </option>)}
              </select>
            </div>
            <div data-id="element-380" data-mp="19md5s2ixotpn">
              <label htmlFor="cu-zip" className="sr-only" data-id="element-381" data-mp="19mvazonkqi9p">
                ZIP code
              </label>
              <input id="cu-zip" type="text" inputMode="numeric" autoComplete="postal-code" placeholder="ZIP code" value={form.zipCode} onChange={e => update('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))} className={inputClass} data-id="element-382" data-mp="1elg12v943lkf" />
            </div>
          </div>
        </div> : <div className="mt-4 sm:mt-5 space-y-3" data-id="element-383" data-mp="zxkxhw6z6akn">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-384" data-mp="uvluinf5dfo8">
            <div data-id="element-385" data-mp="18w6a7aprmvdi">
              <label htmlFor="cu-first" className="sr-only" data-id="element-386" data-mp="18wpuuz6svxv8">
                First name
              </label>
              <input id="cu-first" type="text" autoComplete="given-name" placeholder="First name" value={form.firstName} onChange={e => update('firstName', e.target.value)} className={inputClass} data-id="element-387" data-mp="14vw7c6x9sl2v" />
            </div>
            <div data-id="element-388" data-mp="pzevzfp2z6gb">
              <label htmlFor="cu-last" className="sr-only" data-id="element-389" data-mp="l73vobqfhin8">
                Last name
              </label>
              <input id="cu-last" type="text" autoComplete="family-name" placeholder="Last name" value={form.lastName} onChange={e => update('lastName', e.target.value)} className={inputClass} data-id="element-390" data-mp="3tm5g7en92udh" />
            </div>
          </div>
          <div data-id="element-391" data-mp="3k96dwz6pvsbg">
            <label htmlFor="cu-email" className="sr-only" data-id="element-392" data-mp="3k9ojwnj6lkwk">
              Email address
            </label>
            <input id="cu-email" type="email" autoComplete="email" placeholder="Email address" value={form.email} onChange={e => update('email', e.target.value)} className={inputClass} data-id="element-393" data-mp="3ai1zc0kye9h5" />
          </div>
          <div data-id="element-394" data-mp="3o9p5cs4p6wd4">
            <label htmlFor="cu-phone" className="sr-only" data-id="element-395" data-mp="3oa8crewq22cw">
              Phone number
            </label>
            <input id="cu-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="Phone number" value={form.phone} onChange={e => update('phone', formatPhone(e.target.value))} className={inputClass} data-id="element-396" data-mp="3t7idhqpka0g1" />
          </div>
        </div>}

      {/* Consent language belongs on the step where the lead is actually
          submitted, so it sits directly above that action. Jornaya's checkbox
          disclosure pattern: the input carries the disclosure id, inside a
          label wrapping the text. */}
      {step === 2 && <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-id="element-397" data-mp="2htbtwub2heea">
          <input id="leadid_tcpa_disclosure" type="checkbox" checked={consent} onChange={e => {
        setConsent(e.target.checked);
        if (error) setError('');
      }} className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 cursor-pointer focus:ring-2 focus:ring-offset-1" style={{
        accentColor: CRESTMONT.navy
      }} data-id="element-398" data-mp="2hvfxfb520be1" />
          <span className="text-[10px] leading-relaxed text-gray-500" data-id="element-399" data-mp="2vtcx4get0xwx">
            {university?.tcpaText}
          </span>
        </label>}

      {error && <p role="alert" className="mt-4 text-sm text-red-600" data-id="element-400" data-mp="icjn4mtyr7u9">
          {error}
        </p>}

      <button type="submit" disabled={isSubmitting || addressChecking || step === 2 && !consent} className="mt-5 w-full py-3.5 sm:py-3 rounded text-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00426b]" style={{
      backgroundColor: CRESTMONT.gold,
      color: CRESTMONT.ink
    }} data-id="element-401" data-mp="nbnv5a55izj0">
        {step === 0 ? 'Continue' : step === 1 ? addressChecking ? 'Checking your address…' : 'Continue' : isSubmitting ? 'Sending…' : 'Request Information'}
      </button>

      {step > 0 && <button type="button" onClick={() => {
      setError('');
      setStep(step - 1);
    }} className="mt-3 mx-auto flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00426b] rounded" data-id="element-402" data-mp="1oynj4641kd5d">
          <ArrowLeftIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-403" data-mp="1n0tnmsxakwn7" />
          Back
        </button>}
    </form>;
}